import { computed, inject, Injectable, Signal } from '@angular/core';
import { ConfigurationStore } from '../../api/configuration/configuration-store.service';
import { Router } from '@angular/router';
import {
  ITicketAuthChoiceDialogData,
  TicketAuthChoiceDialogComponent,
} from '../../../components/dialogs/ticket-auth-choice-dialog/ticket-auth-choice-dialog.component';
import {
  IProductInCart,
  ShoppingCartService,
} from '../../shopping-cart/shopping-cart.service';
import {
  ITicketDefinitionForm,
  ShoppingCartFormService,
} from '../../shopping-cart/shopping-cart-form.service';
import { DialogService } from '../dialog/dialog.service';
import {
  AUTHORIZE_PAYMENT,
  CONFIGURATION_SETTING_KEYS,
  EMessageCode,
  EMessageType,
  IDirectTicket,
  IJourneyLeg,
  IJourneyTrip,
  IOperatorMapping,
  TariffProductGroup,
} from '@web/dtos';
import { ReconRouteStore } from '../../api/journey-planning/recon/recon-route-store.service';
import { OperatorMappingStore } from '../../api/operator-mapping/operator-mapping-store.service';
import { MessageService } from '../../messages/messages.service';
import { FormArray, FormGroup } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthProviderFactory } from '../../auth/auth-provider.factory';
import { AuthProvider } from '../../auth/auth.provider';
import { ERoute } from '../../../constants/routes.enum';

@Injectable({
  providedIn: 'root',
})
export class OrderDirectTicketSupportService {
  private readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly reconRouteStore: ReconRouteStore = inject(ReconRouteStore);
  private readonly operatorMappingStore: OperatorMappingStore =
    inject(OperatorMappingStore);
  private readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  private readonly shoppingCartFormService: ShoppingCartFormService = inject(
    ShoppingCartFormService
  );
  private readonly messageService: MessageService = inject(MessageService);
  private readonly router: Router = inject(Router);
  private readonly dialogService: DialogService = inject(DialogService);

  public readonly ticketConfiguration: Signal<TariffProductGroup> = this
    .shoppingCartService
    .directTicketConfiguration as Signal<TariffProductGroup>;
  protected readonly productForms: FormArray<FormGroup<ITicketDefinitionForm>> =
    this.shoppingCartService.productForms;
  protected readonly ticketsInCart: Signal<IProductInCart[]> =
    this.shoppingCartService.ticketsInCart;
  protected readonly productFormsValue: Signal<
    FormGroup<ITicketDefinitionForm>['value'][] | undefined
  > = toSignal(this.productForms.valueChanges);
  private readonly isOnDemandTravel: Signal<boolean> = computed(() => {
    const route: IJourneyTrip = this.reconRouteStore.reconRoute();
    const operatorIds: string[] = this.operatorMappingStore
      .operatorMappings()
      .map((operator: IOperatorMapping) => operator.operatorId);
    if (!operatorIds || !route) {
      return false;
    }
    const operatorIdsOnRoute: string[] = route.legs.map(
      (leg: IJourneyLeg) => leg.operator.id
    );
    let isOnDemand = false;
    operatorIdsOnRoute.forEach((operatorId: string) => {
      if (operatorIds.includes(operatorId)) {
        isOnDemand = true;
      }
    });
    return isOnDemand;
  });

  public tryToOrderDirectTicket(ticketName: string, isRelation: boolean): void {
    localStorage.removeItem(AUTHORIZE_PAYMENT);
    const isReconRoute: boolean = this.router.url.startsWith(
      `/${ERoute.directTickets}/recon-id/`
    );
    const ticketUrl: string =
      isReconRoute && this.isOnDemandTravel() && isRelation
        ? '/on-demand-ticket-configuration/' + ticketName
        : '/ticket-configuration/' + ticketName;
    const isDirectGuestSaleActive: boolean =
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsDirectSaleGuestActive
      );
    const isUserLoggedIn: boolean = this.auth.isLoggedIn();
    const canBeAddedToCart: boolean =
      this.shoppingCartService.totalNumberOfTicketsInCart <
      this.configurationStore.getNumberConfiguration(
        CONFIGURATION_SETTING_KEYS.MaxShoppingCartSize
      );
    this.shoppingCartService.continueEditingExistingTicket.set(false);
    if (
      (isUserLoggedIn ||
        this.shoppingCartService.productForms.controls.length > 0) &&
      canBeAddedToCart
    ) {
      this.addNewDirectTicketForm();
      this.router.navigate([ticketUrl]);
    } else if (!isUserLoggedIn && isDirectGuestSaleActive) {
      this.dialogService.openDialog<ITicketAuthChoiceDialogData>(
        TicketAuthChoiceDialogComponent,
        {
          headline: '',
          width: '400px',
          ticketUrl,
        }
      );
    } else if (!isUserLoggedIn) {
      this.auth.login(ticketUrl);
    } else if (!canBeAddedToCart) {
      this.messageService.add({
        type: EMessageType.ERROR,
        code: EMessageCode.TICKET_CANNOT_BE_ADDED,
        params: {
          maxAmount: this.configurationStore.getNumberConfiguration(
            CONFIGURATION_SETTING_KEYS.MaxShoppingCartSize
          ),
        },
        text: {
          code: EMessageCode.TICKET_CANNOT_BE_ADDED,
          text: {
            values: [],
          },
        },
      });
    }
  }

  public goToDirectTicket(ticketUrl?: string): void {
    const isDirectGuestSaleActive: boolean =
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsDirectSaleGuestActive
      );
    if (isDirectGuestSaleActive && ticketUrl) {
      this.addNewDirectTicketForm();
      this.router.navigate([ticketUrl]);
    } else {
      console.warn(
        `Cannot go to direct guest sale. <br> 
        Guest Sale active ${isDirectGuestSaleActive} <br>
        Url: ${ticketUrl}
      `
      );
    }
  }

  public setCurrentProductDefinition(): void {
    if (!this.productFormsValue()?.[0]?.product && this.ticketConfiguration()) {
      this.shoppingCartService.setCurrentProductDefinition(
        this.ticketConfiguration().productDefinitions[0],
        this.ticketConfiguration()
          .productDefinitions[0].keyElements.filter(
            attribute => attribute.name.toLowerCase() !== 'name'
          )
          .map(k => k.value),
        this.ticketConfiguration()
          .productDefinitions[0].keyElements.filter(
            attribute => attribute.name.toLowerCase() === 'name'
          )
          .map(k => k.value)
      );
      this.productForms.controls[0].controls.tariffGroup.setValue(
        this.ticketConfiguration()
      );
      this.productForms.updateValueAndValidity();
    }
  }

  public tryToAddToCart(): void {
    const ticket: IDirectTicket | undefined = this.shoppingCartService.ticket();
    if (
      ticket &&
      this.shoppingCartService.productForms.controls.length >
        this.ticketsInCart().length
    ) {
      this.shoppingCartService.addToCart({
        ...this.shoppingCartService.productForms.controls[0].value.product!,
        name: ticket.name,
        tariffProductGroup: {
          name: ticket.name,
          selectionAttributes: ticket.selectionAttributes,
          productDefinitions: ticket.productDefinitions,
        },
        productForm: this.shoppingCartService.productForms.controls[0],
        ticket: this.shoppingCartService.ticket()?.ticketData,
        owner: this.shoppingCartService.ticketOwnerDataForms.controls[0],
      });
    }
  }

  private addNewDirectTicketForm(): void {
    this.shoppingCartService.productForms.controls.unshift(
      this.shoppingCartFormService.createProductForm([], [])
    );
    this.shoppingCartService.ticketOwnerDataForms.controls.unshift(
      this.shoppingCartFormService.createTicketOwnerDataForms()
    );
  }
}
